import { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { getUserCauses, getRecentActivities, logActivity, addUserCause, removeUserCause } from '../firebase/services';

// Create the data context
export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [causes, setCauses] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Fetch user data when the user changes
  useEffect(() => {
    const fetchData = async () => {
      if (currentUser) {
        setLoading(true);
        setError(null);

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Data fetch timeout')), 10000)
        );

        const dataFetchPromise = Promise.all([
          getUserCauses(currentUser.uid),
          getRecentActivities(currentUser.uid, 5),
        ]);

        try {
          const [userCauses, { activities: userActivities, lastVisible: newLastVisible }] = await Promise.race([
            dataFetchPromise,
            timeoutPromise,
          ]);
          
          const uniqueCauses = userCauses.filter((cause, index, self) =>
            index === self.findIndex((c) => c.id === cause.id)
          );

          setCauses(uniqueCauses);
          setActivities(userActivities);
          setLastVisible(newLastVisible);
          setHasMore(userActivities.length > 0);

        } catch (error) {
          console.error('DataContext Error: Failed to fetch user data.', error);
          setError('Failed to load user data. Please try again later.');
        } finally {
          setLoading(false);
        }
      } else {
        // Reset data when user is not authenticated
        setCauses([]);
        setActivities([]);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, lastUpdated]);

  // Function to refresh data
  const refreshData = async () => {
    if (currentUser) {
      setLoading(true);
      setError(null);
      
      try {
        const userCauses = await getUserCauses(currentUser.uid);
        const { activities: userActivities, lastVisible: newLastVisible } = await getRecentActivities(currentUser.uid, 5);
        
        setCauses(userCauses);
        setActivities(userActivities);
        setLastVisible(newLastVisible);
        setHasMore(userActivities.length > 0);
      } catch (error) {
        console.error('Error refreshing user data:', error);
        setError('Failed to refresh user data. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
  };

  const addActivity = async (activityData, processedImages = null, onProgress) => {
    try {
      const savedActivity = await logActivity(activityData, processedImages, onProgress);
      setLastUpdated(Date.now());
      return savedActivity;
    } catch (error) {
      console.error("DataContext Error: Failed to add activity.", error);
      setError("Failed to log activity. Please try again.");
      throw error;
    }
  };

  const addCause = async (causeData) => {
    if (!currentUser) {
      throw new Error("User not authenticated");
    }
    try {
      await addUserCause(currentUser.uid, causeData);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error("DataContext: Error adding cause:", error);
      setError("Failed to add cause. Please try again.");
      throw error;
    }
  };

  const removeCause = async (causeId) => {
    try {
      await removeUserCause(causeId);
      setCauses((prevCauses) => prevCauses.filter((cause) => cause.docId !== causeId));
      setLastUpdated(Date.now());
    } catch (error) {
      console.error("DataContext: Error removing cause:", error);
      setError("Failed to remove cause. Please try again.");
      throw error;
    }
  };

  const loadMoreActivities = async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    try {
      const { activities: newActivities, lastVisible: newLastVisible } = await getRecentActivities(currentUser.uid, 5, lastVisible);
      setActivities(prev => [...prev, ...newActivities]);
      setLastVisible(newLastVisible);
      setHasMore(newActivities.length > 0);
    } catch (error) {
      console.error('DataContext Error: Failed to load more activities.', error);
      setError('Failed to load more activities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Remove an activity from local state (optimistic delete)
  const removeActivityFromState = (activityId) => {
    setActivities(prev => prev.filter(a => a.id !== activityId));
  };

  // Update an activity in local state (optimistic edit)
  const updateActivityInState = (activityId, updatedFields) => {
    setActivities(prev => prev.map(a =>
      a.id === activityId ? { ...a, ...updatedFields } : a
    ));
  };

  // Restore an activity to local state (undo delete)
  const restoreActivityToState = (activity) => {
    setActivities(prev => {
      const updated = [...prev, activity];
      // Re-sort by timestamp descending to maintain order
      updated.sort((a, b) => {
        const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return timeB - timeA;
      });
      return updated;
    });
  };

  // Context value
  const value = {
    causes,
    activities,
    loading,
    error,
    refreshData,
    addActivity,
    addCause,
    removeCause,
    lastUpdated,
    loadMoreActivities,
    hasMore,
    removeActivityFromState,
    updateActivityInState,
    restoreActivityToState,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
